import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { FileText, ServerCrash } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { Toast } from '@/common/components/Toast'
import { api, ApiError } from '@/lib/axios'
import { queryClient } from '@/lib/tanstack'
import { NewGenericContentForm } from '../schemas/NewGenericContentSchema'

type PostNewGenericContentProps = {
  tagContent: string
  contentType: string[]
  relativeKeys: string[]
  searchTags: string[]
  fieldKey: string
  fieldValue: string
  infoMessage: string
}

async function postNewGenericContent(data: PostNewGenericContentProps) {
  return api.backend.post('/generic-content', data)
}

export function useCreateGenericContent() {
  const { t } = useTranslation('settings')

  const { mutateAsync, isPending } = useMutation<
    unknown,
    AxiosError<ApiError>,
    PostNewGenericContentProps
  >({
    mutationKey: ['/generic-content'],
    mutationFn: postNewGenericContent,
    onSuccess: () => {
      toast(Toast, {
        data: {
          type: 'success',
          icon: FileText,
          title: t(
            'generic_content.drawer_new_generic_content.toast_success.title',
          ),
        },
      })
      queryClient.invalidateQueries({
        queryKey: ['generic-content/paginate'],
      })
    },
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: ServerCrash,
          title: t(
            'generic_content.drawer_new_generic_content.toast_error.title',
          ),
          description:
            error?.response?.data.friendlyMessage ||
            t(
              'generic_content.drawer_new_generic_content.toast_error.description',
            ),
        },
      })
    },
  })

  const handleCreateGenericContent = async (data: NewGenericContentForm) => {
    const payload: PostNewGenericContentProps = {
      tagContent: data.tagContent,
      contentType: Array.isArray(data.contentType)
        ? data.contentType.map((item) => item.value || item.label || '')
        : [],
      relativeKeys: Array.isArray(data.relativeKeys)
        ? data.relativeKeys.map((item) => item.value || item.label || '')
        : [],
      searchTags: Array.isArray(data.searchTags)
        ? data.searchTags.map((item) => item.value || item.label || '')
        : [],
      fieldKey: data.fieldKey,
      fieldValue: data.fieldValue,
      infoMessage: data.infoMessage || '',
    }

    return mutateAsync(payload)
  }

  return {
    handleCreateGenericContent,
    isCreatingGenericContent: isPending,
  }
}
